/**
 * The feed's data model and its optimistic reconciliation - pure functions,
 * no React, no I/O.
 *
 * A feed post is written twice: once locally the instant the user hits Post
 * (so the UI does not stall on a round-trip) and once by the server when the
 * refresh that follows the POST comes back. Everything here exists to make
 * those two copies collapse into exactly one - never zero (a lost post) and
 * never two (a ghost). That is why this file is pure and covered by tests:
 * it is the one place in the feed where a rewrite can silently drop a post.
 */

export interface FeedUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  profile_picture?: string | null;
}

export interface FeedComment {
  id?: string;
  message: string;
  timestamp?: string;
  created_by_user_id?: string | number;
  created_by?: FeedUser;
  isOptimistic?: boolean;
}

export interface FeedEvent {
  id?: string;
  event_type: string;
  message: string;
  timestamp?: string;
  created_by_user_id?: string | number;
  created_by_name?: string;
  created_by?: FeedUser;
  metadata?: any;
  member?: FeedUser;
  comments?: FeedComment[];
  isOptimistic?: boolean;
  is_pinned?: boolean;
  is_edited?: boolean;
}

/** Anything that carries an author - an event or a comment. */
export interface FeedAuthored {
  created_by?: FeedUser;
  created_by_user_id?: string | number;
}

export type FeedTimeFilter = 'all' | 'today' | 'week' | 'month';

export interface FeedFilters {
  /** An event_type, or 'all'. */
  type: string;
  /** A member id, or 'all'. */
  person: string;
  /** One of FeedTimeFilter; anything else is ignored rather than trusted. */
  time: string;
}

export const FEED_ALL = 'all';

export const FEED_FILTERS_NONE: FeedFilters = {
  type: FEED_ALL,
  person: FEED_ALL,
  time: FEED_ALL,
};

const VALID_TIME_FILTERS: FeedTimeFilter[] = ['all', 'today', 'week', 'month'];

/** Post types offered by the composer when a page does not name its own. */
export const DEFAULT_POST_TYPES = ['update', 'action', 'milestone', 'issue', 'note'];

/** Separator that cannot occur in a type, a message or an id. */
const SEP = ' ';

/**
 * Parse a backend timestamp as UTC. Timestamps are stored in UTC but are not
 * always serialized with a zone; without the explicit `Z` the browser reads a
 * naked `2026-08-08 10:00:00` as local time and every relative age is off by
 * the user's offset.
 */
export const parseFeedTimestamp = (timestamp: string): Date => {
  const trimmed = timestamp.trim();
  if (/Z$/i.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }
  return new Date(trimmed.replace(' ', 'T') + 'Z');
};

/** The author's id as a string, whichever field the backend filled in. */
export const authorId = (entity: FeedAuthored | undefined): string | undefined => {
  const raw = entity?.created_by?.id ?? entity?.created_by_user_id;
  return raw === undefined || raw === null || raw === '' ? undefined : String(raw);
};

/** Ids arrive as strings from one endpoint and numbers from another. */
export const isSameAuthor = (a: FeedAuthored | undefined, b: FeedAuthored | undefined): boolean =>
  authorId(a) === authorId(b);

/** Two posts are the same post if the type, the text and the author all match. */
export const eventDedupKey = (event: FeedEvent): string =>
  [event.event_type ?? '', event.message ?? '', authorId(event) ?? ''].join(SEP);

/** Comments have no type, so text + author is the whole identity. */
export const commentDedupKey = (comment: FeedComment): string =>
  [comment.message ?? '', authorId(comment) ?? ''].join(SEP);

/**
 * Consume `keys` one at a time: two identical pending posts must be confirmed
 * by two server rows, not by the same row counted twice.
 */
const makeKeyPool = (keys: string[]) => {
  const pool = new Map<string, number>();
  keys.forEach((key) => pool.set(key, (pool.get(key) || 0) + 1));
  return (key: string): boolean => {
    const left = pool.get(key) || 0;
    if (left <= 0) return false;
    pool.set(key, left - 1);
    return true;
  };
};

/**
 * The optimistic posts the server has not echoed back yet - the ones that must
 * stay on screen. A failed or stale refresh means nothing matches, so nothing
 * is dropped.
 */
export const unconfirmedEvents = (
  optimistic: FeedEvent[] | undefined,
  server: FeedEvent[] | undefined
): FeedEvent[] => {
  if (!optimistic?.length) return [];
  const take = makeKeyPool((server || []).map(eventDedupKey));
  return optimistic.filter((event) => !take(eventDedupKey(event)));
};

/** The list the feed renders: pending posts on top, then the server's. */
export const mergeFeedEvents = (
  optimistic: FeedEvent[] | undefined,
  server: FeedEvent[] | undefined
): FeedEvent[] => [...unconfirmedEvents(optimistic, server), ...(server || [])];

/** The pending comments on one event that the server has not echoed back yet. */
export const unconfirmedComments = (
  pending: FeedComment[] | undefined,
  server: FeedComment[] | undefined
): FeedComment[] => {
  if (!pending?.length) return [];
  const take = makeKeyPool((server || []).map(commentDedupKey));
  return pending.filter((comment) => !take(commentDedupKey(comment)));
};

/** Server comments first (they are older), then whatever is still in flight. */
export const mergeEventComments = (
  server: FeedComment[] | undefined,
  pending: FeedComment[] | undefined
): FeedComment[] => [...(server || []), ...unconfirmedComments(pending, server)];

/** What the "Reply · N" counter shows. */
export const commentCount = (
  server: FeedComment[] | undefined,
  pending: FeedComment[] | undefined
): number => mergeEventComments(server, pending).length;

const withinTimeFilter = (event: FeedEvent, time: string, now: Date): boolean => {
  // A post that has not been stamped yet is in flight - never hide it.
  if (!event.timestamp) return true;
  const date = parseFeedTimestamp(event.timestamp);
  if (isNaN(date.getTime())) return true;
  switch (time) {
    case 'today':
      return date.toDateString() === now.toDateString();
    case 'week':
      return date >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case 'month':
      return date >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    default:
      return true;
  }
};

export const filterFeedEvents = (
  events: FeedEvent[],
  filters: FeedFilters,
  now: Date = new Date()
): FeedEvent[] => {
  const timeFilter = VALID_TIME_FILTERS.includes(filters.time as FeedTimeFilter)
    ? filters.time
    : FEED_ALL;

  return events.filter((event) => {
    if (filters.type !== FEED_ALL && event.event_type !== filters.type) return false;
    if (filters.person !== FEED_ALL && authorId(event) !== filters.person) return false;
    if (timeFilter !== FEED_ALL && !withinTimeFilter(event, timeFilter, now)) return false;
    return true;
  });
};

/** The event types actually present, for the Type filter. */
export const uniqueEventTypes = (events: FeedEvent[]): string[] =>
  Array.from(new Set(events.map((e) => e.event_type).filter(Boolean))).sort();

export const hasActiveFilters = (filters: FeedFilters): boolean =>
  filters.type !== FEED_ALL || filters.person !== FEED_ALL || filters.time !== FEED_ALL;

const asAuthor = (user: FeedUser): FeedUser => ({
  id: user.id,
  first_name: user.first_name,
  last_name: user.last_name,
  email: user.email,
  profile_picture: user.profile_picture ?? null,
});

export const makeOptimisticEvent = ({
  eventType,
  message,
  user,
  id,
  timestamp,
}: {
  eventType: string;
  message: string;
  user: FeedUser;
  id: string;
  timestamp: string;
}): FeedEvent => ({
  id,
  event_type: eventType,
  message,
  timestamp,
  isOptimistic: true,
  created_by: asAuthor(user),
});

export const makeOptimisticComment = ({
  message,
  user,
  id,
  timestamp,
}: {
  message: string;
  user: FeedUser;
  id: string;
  timestamp: string;
}): FeedComment => ({
  id,
  message,
  timestamp,
  isOptimistic: true,
  created_by: asAuthor(user),
});
